import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {CSSProperties, ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {TabPanel, TabView} from "primereact/tabview";
import i18next from "i18next";
import {Dropdown} from "primereact/dropdown";
import {WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputNumber} from "primereact/inputnumber";
import {Slider} from "primereact/slider";
import {InputSwitch} from "primereact/inputswitch";
import ImageUnit from "../../../../shared/units/ImageUnit";
import PaperHelper, {PaperFormat, paperSizes} from "../../../../shared/helper/PaperHelper";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import OcrDialogFields from "../../field/convert/OcrDialogFields";
import {PREVIEW_SIZE} from "../../../startscreen/Preview";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	ConvertPdfa,
	Functions,
	ImageOptimization,
	Information,
	Ocr,
	OcrLanguage,
	OcrOutput,
	OcrPage,
	OptimizationIntensity,
	Security,
	Settings,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {OcrPdfaType, PortalOcr} from "../../../../shared/models/PortalOcr";
import {PortalOcrPage} from "../../../../shared/models/PortalOcrPage";
import FormatEnum from "../../../../shared/models/FormatEnum";
import OrientationEnum from "../../../../shared/models/OrientationEnum";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import {Checkbox} from "primereact/checkbox";
import Icon from "../../../../shared/components/Icon";
import UserSettings from "../../../session/UserSettings";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import ConcurrentOperation from "../../../../shared/class/ConcurrentOperation";
import Exception from "../../../../shared/exception/Exception";

interface OcrDialogProperties extends PortalDialogProperties<PortalOcr> {
	userSettings: Immutable<UserSettings>;
}

interface OcrDialogStatus extends PortalDialogState<PortalOcr> {
	panel: number;
	unit: LengthUnit;
}

export class OcrDialogDefinition extends PortalDialog<PortalOcr, OcrDialogProperties, OcrDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			panel: 0,
			fieldErrors: new Map<OcrDialogFields, string>(),
			unit: LengthUnit.MILLIMETER
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.ocr?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.ocrConversion, Functions.getOcrConversionDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.OCR, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length >= 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [
				MimeTypes.PDF, MimeTypes.PNG, MimeTypes.JPEG, MimeTypes.TIFF
			]);
	}

	public renderGeneralTab(parameter: PortalOcr): ReactNode {
		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OcrDialogFields.OUTPUT_FORMAT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-output-format">
							<Translate {...this.props} data="ocr.general.output.format.label" namespace="dialog"/>
							<Info {...this.props} data="ocr.general.output.format.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-output-format"
									  value={FunctionHelper.applyValue(
										  parameter.outputFormat, Ocr.getOutputFormatDefault()
									  )}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.OUTPUT_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.general.output.format.pdf", {ns: "dialog"}
											  ),
											  value: OcrOutput.Pdf
										  },
										  {
											  label: Translate.value(
												  "ocr.general.output.format.text", {ns: "dialog"}
											  ),
											  value: OcrOutput.Text
										  },
										  {
											  label: Translate.value(
												  "ocr.general.output.format.xml", {ns: "dialog"}
											  ),
											  value: OcrOutput.Hocr
										  }
									  ]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.outputFormat !== OcrOutput.Pdf ||
						!this.state.fields?.contains(OcrDialogFields.PDFA_FORMAT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-format">
							<Translate {...this.props} data="ocr.general.pdfa.format.label" namespace="dialog"/>
							<Info {...this.props} data="ocr.general.pdfa.format.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-pdfa-format"
									  value={FunctionHelper.applyValue(
										  parameter.pdfaMode, PortalOcr.getPdfaModeDefault()
									  )}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.PDFA_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.none", {ns: "dialog"}
											  ),
											  value: OcrPdfaType.NONE
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.1a", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._1A
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.1b", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._1B
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.2a", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._2A
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.2b", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._2B
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.2u", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._2U
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.3a", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._3A
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.3b", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._3B
										  },
										  {
											  label: Translate.value(
												  "ocr.general.pdfa.format.3u", {ns: "dialog"}
											  ),
											  value: OcrPdfaType._3U
										  }
									  ]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 parameter.outputFormat !== OcrOutput.Pdf || parameter.pdfaMode === OcrPdfaType.NONE ||
							 !this.state.fields?.contains(OcrDialogFields.PDFA_RESOLUTION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-resolution">
							<Translate {...this.props} data="ocr.general.pdfa.resolution" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 slider-input">
							<InputNumber inputId="control-pdfa-resolution" step={1} suffix=" %" showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter.pdfa?.convert?.imageQuality, ConvertPdfa.getImageQualityDefault()
										 )}
										 min={ConvertPdfa.getImageQualityMin()} max={ConvertPdfa.getImageQualityMax()}
										 onValueChange={(e) => this.onChange(
											 OcrDialogFields.PDFA_RESOLUTION, e.value
										 )}/>
							<Slider min={ConvertPdfa.getImageQualityMin()}
									max={ConvertPdfa.getImageQualityMax()} step={1}
									value={FunctionHelper.applyValue(
										parameter.pdfa?.convert?.imageQuality, ConvertPdfa.getImageQualityDefault()
									)}
									onChange={(e) => this.onChange(
										OcrDialogFields.PDFA_RESOLUTION, e.value as number
									)}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OcrDialogFields.LANGUAGE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-language">
							<Translate {...this.props} data="ocr.general.properties.language.label" namespace="dialog"/>
							<Info {...this.props} data="ocr.general.properties.language.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-language"
									  value={FunctionHelper.applyValue(parameter.language, Ocr.getLanguageDefault())}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.LANGUAGE, e.value
									  )}
									  appendTo={document.getElementById("root")}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.general.properties.language.spanish", {ns: "dialog"}
											  ),
											  value: OcrLanguage.Spa
										  },
										  {
											  label: Translate.value(
												  "ocr.general.properties.language.french", {ns: "dialog"}
											  ),
											  value: OcrLanguage.Fra
										  },
										  {
											  label: Translate.value(
												  "ocr.general.properties.language.italian", {ns: "dialog"}
											  ),
											  value: OcrLanguage.Ita
										  },
										  {
											  label: Translate.value(
												  "ocr.general.properties.language.german", {ns: "dialog"}
											  ),
											  value: OcrLanguage.Deu
										  },
										  {
											  label: Translate.value(
												  "ocr.general.properties.language.english", {ns: "dialog"}
											  ),
											  value: OcrLanguage.Eng
										  }
									  ]}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.outputFormat !== OcrOutput.Pdf ||
						!this.state.fields?.contains(OcrDialogFields.JPEG_QUALITY)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-jpeg-quality">
							<Translate {...this.props} data="ocr.general.properties.jpeg_quality.label"
									   namespace="dialog"/>
							<Info {...this.props} data="ocr.general.properties.jpeg_quality.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 slider-input">
							<InputNumber inputId="control-jpeg-quality" step={1} suffix=" %" showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter.jpegQuality, Ocr.getJpegQualityDefault()
										 )}
										 min={Ocr.getJpegQualityMin()} max={Ocr.getJpegQualityMax()}
										 onValueChange={(e) => this.onChange(
											 OcrDialogFields.JPEG_QUALITY, e.value
										 )}/>
							<Slider step={1} max={Ocr.getJpegQualityMax()} min={Ocr.getJpegQualityMin()}
									value={FunctionHelper.applyValue(
										parameter.jpegQuality, Ocr.getJpegQualityDefault()
									)}
									onChange={(e) => this.onChange(
										OcrDialogFields.JPEG_QUALITY, e.value
									)}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.outputFormat !== OcrOutput.Pdf || !this.state.fields?.contains(OcrDialogFields.DPI)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-dpi">
							<Translate {...this.props} data="ocr.general.properties.dpi.label" namespace="dialog"/>
							<Info {...this.props} data="ocr.general.properties.dpi.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<span className="p-inputgroup-addon p-inputgroup-addon-checkbox">
									<Checkbox
										checked={FunctionHelper.applyValue(
											parameter.imageDpiActive, Ocr.getImageDpiDefault()
										)}
										onChange={() => this.onChange(
											OcrDialogFields.DPI_ACTIVE, !parameter.imageDpiActive
										)}/>
								</span>
								<InputNumber inputId="control-dpi" showButtons={true} step={1} useGrouping={false}
											 value={FunctionHelper.applyValue(
												 parameter.imageDpi, Ocr.getImageDpiDefault()
											 )}
											 min={Ocr.getImageDpiMin()} max={Ocr.getImageDpiMax()}
											 suffix={" " + ImageUnit.DPI} disabled={!parameter.imageDpiActive}
											 onChange={(e) => this.onChange(
												 OcrDialogFields.DPI, e.value
											 )}/>
							</div>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OcrDialogFields.NORMALIZE_ROTATION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-normalize"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-normalize"
										 checked={FunctionHelper.applyValue(
											 parameter.normalizePageRotation, Ocr.getNormalizePageRotationDefault()
										 )}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.NORMALIZE_ROTATION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-normalize">
								<Translate {...this.props} data="ocr.general.properties.normalize_rotation.label"
										   namespace="dialog"/>
								<Info {...this.props} data="ocr.general.properties.normalize_rotation.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OcrDialogFields.CHECK_RESOLUTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-check-resolution"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-check-resolution"
										 checked={FunctionHelper.applyValue(
											 parameter.checkResolution, Ocr.getCheckResolutionDefault()
										 )}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.CHECK_RESOLUTION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-check-resolution">
								<Translate {...this.props} data="ocr.general.properties.check_resolution.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OcrDialogFields.FAIL_ON_WARNING)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-fail-on-warning"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-fail-on-warning"
										 checked={FunctionHelper.applyValue(
											 parameter.failOnWarning, Ocr.getFailOnWarningDefault()
										 )}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.FAIL_ON_WARNING, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-fail-on-warning">
								<Translate {...this.props} data="ocr.general.properties.fail_on_warning.label"
										   namespace="dialog"/>
								<Info {...this.props} data="ocr.general.properties.fail_on_warning.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderOptimizationTab(parameter: PortalOcr): ReactNode {
		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OcrDialogFields.OPTIMIZE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-optimize"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-optimize"
										 checked={FunctionHelper.applyValue(
											 parameter.optimizationActive, PortalOcr.getOptimizationActiveDefault()
										 )}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.OPTIMIZE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-optimize">
								<Translate {...this.props} data="ocr.optimization.optimize.label" namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.NOISE_REDUCTION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-noise-reduction">
							<Translate {...this.props} data="ocr.optimization.noise_reduction.label"
									   namespace="dialog"/>
							<Info {...this.props} data="ocr.optimization.noise_reduction.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-noise-reduction"
									  value={FunctionHelper.applyValue(
										  parameter.optimization?.noiseReduction,
										  ImageOptimization.getNoiseReductionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.optimization.options.off", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Off
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.low", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Low
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.medium", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Medium
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.high", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.High
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.NOISE_REDUCTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.GAMMA_CORRECTION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-gamma-correction">
							<Translate {...this.props} data="ocr.optimization.gamma_correction.label"
									   namespace="dialog"/>
							<Info {...this.props} data="ocr.optimization.gamma_correction.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-gamma-correction"
									  value={FunctionHelper.applyValue(
										  parameter.optimization?.gammaCorrection,
										  ImageOptimization.getGammaCorrectionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.optimization.options.off", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Off
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.low", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Low
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.medium", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Medium
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.high", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.High
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.GAMMA_CORRECTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.INCREASE_CONTRAST)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-increase-contrast">
							<Translate {...this.props} data="ocr.optimization.increase_contrast.label"
									   namespace="dialog"/>
							<Info {...this.props} data="ocr.optimization.increase_contrast.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-increase-contrast"
									  value={FunctionHelper.applyValue(
										  parameter.optimization?.increaseContrast,
										  ImageOptimization.getIncreaseContrastDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.optimization.options.off", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Off
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.low", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Low
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.medium", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Medium
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.high", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.High
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.INCREASE_CONTRAST, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.EDGE_ACCENTUATION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-edge-accentuation">
							<Translate {...this.props} data="ocr.optimization.edge_accentuation.label"
									   namespace="dialog"/>
							<Info {...this.props} data="ocr.optimization.edge_accentuation.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-edge-accentuation"
									  value={FunctionHelper.applyValue(
										  parameter.optimization?.edgeAccentuation,
										  ImageOptimization.getEdgeAccentuationDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.optimization.options.off", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Off
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.low", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Low
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.medium", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Medium
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.high", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.High
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.EDGE_ACCENTUATION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.SHARPEN)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-sharpen">
							<Translate {...this.props} data="ocr.optimization.sharpen.label" namespace="dialog"/>
							<Info {...this.props} data="ocr.optimization.sharpen.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-sharpen"
									  value={FunctionHelper.applyValue(
										  parameter.optimization?.sharpen, ImageOptimization.getSharpenDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.optimization.options.off", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Off
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.low", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Low
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.medium", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Medium
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.high", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.High
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.SHARPEN, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.MEDIAN_FILTER)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-median-filter">
							<Translate {...this.props} data="ocr.optimization.median_filter.label" namespace="dialog"/>
							<Info {...this.props} data="ocr.optimization.median_filter.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-median-filter"
									  value={FunctionHelper.applyValue(
										  parameter.optimization?.medianFilter,
										  ImageOptimization.getMedianFilterDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.optimization.options.off", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Off
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.low", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Low
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.medium", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.Medium
										  },
										  {
											  label: Translate.value(
												  "ocr.optimization.options.high", {ns: "dialog"}
											  ),
											  value: OptimizationIntensity.High
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.MEDIAN_FILTER, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.DESKEW)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-deskew"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-deskew"
										 checked={FunctionHelper.applyValue(
											 parameter.optimization?.deskew, ImageOptimization.getDeskewDefault()
										 )}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.DESKEW, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-deskew">
								<Translate {...this.props} data="ocr.optimization.deskew.label" namespace="dialog"/>
								<Info {...this.props} data="ocr.optimization.deskew.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.DESPECKLE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-despeckle"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-despeckle"
										 checked={FunctionHelper.applyValue(
											 parameter.optimization?.despeckle, ImageOptimization.getDespeckleDefault()
										 )}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.DESPECKLE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-despeckle">
								<Translate {...this.props} data="ocr.optimization.despeckle.label" namespace="dialog"/>
								<Info {...this.props} data="ocr.optimization.despeckle.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !parameter.optimizationActive ||
							 !this.state.fields?.contains(OcrDialogFields.DITHERING)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-dithering"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-dithering"
										 checked={FunctionHelper.applyValue(
											 parameter.optimization?.reduceDithering,
											 ImageOptimization.getReduceDitheringDefault()
										 )}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.DITHERING, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-dithering">
								<Translate {...this.props} data="ocr.optimization.dithering.label" namespace="dialog"/>
								<Info {...this.props} data="ocr.optimization.dithering.description"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderFormatTab(parameter: PortalOcr): ReactNode {
		let pageParameter: PortalOcrPage = parameter.page as PortalOcrPage;
		let pageFormats: Array<any> = [
			{label: Translate.value("ocr.format.original", {ns: "dialog"}), value: FormatEnum.ORIGINAL},
			{label: Translate.value("ocr.format.custom", {ns: "dialog"}), value: FormatEnum.CUSTOM}
		];

		for (let formatGroup of paperSizes) {
			pageFormats.push({
				label: Translate.value("papersize." + formatGroup.name),
				className: "group",
				value: formatGroup.name,
				disabled: true
			});

			for (let paperFormat of formatGroup.values) {
				pageFormats.push({label: paperFormat.name, className: "item", value: paperFormat.name});
			}
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OcrDialogFields.PAGE_FORMAT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-page-format">
							<Translate {...this.props} data="ocr.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-page-format" options={pageFormats}
									  value={FunctionHelper.applyValue(
										  pageParameter.format, PortalOcrPage.getFormatDefault()
									  )}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.PAGE_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format !== FormatEnum.CUSTOM ||
						!this.state.fields?.contains(OcrDialogFields.WIDTH)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-width">
							<Translate {...this.props} data="ocr.format.width" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(OcrDialogFields.WIDTH)}>
							<InputNumber inputId="control-width" suffix={" " + this.state.unit} showButtons={true}
										 maxFractionDigits={2} minFractionDigits={2} step={1} useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 FunctionHelper.applyValue(pageParameter.width, OcrPage.getWidthDefault()),
											 LengthUnit.MILLIMETER,
											 this.state.unit,
											 this.props.portalResolution
										 )}
										 min={OcrPage.getWidthMin()}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.WIDTH, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						pageParameter.format !== FormatEnum.CUSTOM ||
						!this.state.fields?.contains(OcrDialogFields.HEIGHT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-height">
							<Translate {...this.props} data="ocr.format.height" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={!this.state.fields?.contains(OcrDialogFields.HEIGHT)}>
							<InputNumber inputId="control-height" step={1} suffix={" " + this.state.unit}
										 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
										 useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 FunctionHelper.applyValue(
												 pageParameter.height, OcrPage.getHeightDefault()
											 ),
											 LengthUnit.MILLIMETER,
											 this.state.unit,
											 this.props.portalResolution
										 )}
										 min={OcrPage.getHeightMin()}
										 onChange={(e) => this.onChange(
											 OcrDialogFields.HEIGHT, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 pageParameter.format === FormatEnum.ORIGINAL ||
							 pageParameter.format === FormatEnum.CUSTOM ||
							 !this.state.fields?.contains(OcrDialogFields.ORIENTATION)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-orientation">
							<Translate {...this.props} data="ocr.format.orientation.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-orientation"
									  value={FunctionHelper.applyValue(
										  pageParameter.orientation, PortalOcrPage.getOrientationDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "ocr.format.orientation.vertical", {ns: "dialog"}
											  ),
											  value: OrientationEnum.VERTICAL
										  },
										  {
											  label: Translate.value(
												  "ocr.format.orientation.horizontal", {ns: "dialog"}
											  ),
											  value: OrientationEnum.HORIZONTAL
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OcrDialogFields.ORIENTATION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				{this.renderPreview(pageParameter)}
			</>
		);
	}

	public renderPreview(parameter: PortalOcrPage): ReactNode {
		let scale: number = Math.max(
			FunctionHelper.applyValue(parameter.width, OcrPage.getWidthDefault()),
			FunctionHelper.applyValue(parameter.height, OcrPage.getHeightDefault())
		) / PREVIEW_SIZE;

		let pageStyle: CSSProperties = {
			width: (FunctionHelper.applyValue(parameter.width, OcrPage.getWidthDefault()) / scale) + LengthUnit.PIXEL,
			height: (FunctionHelper.applyValue(parameter.height, OcrPage.getHeightDefault()) / scale) + LengthUnit.PIXEL
		};

		return (
			<fieldset className="p-col-12 p-grid"
					  hidden={
						  parameter.format === FormatEnum.ORIGINAL ||
						  !this.state.fields?.contains(OcrDialogFields.PREVIEW)
					  }>
				<div className="p-col-12 p-grid">
					<div className="page-preview">
						<div style={pageStyle}/>
					</div>
				</div>
			</fieldset>
		);
	}


	public renderContents(): ReactNode {
		let parameter: PortalOcr | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<TabView activeIndex={this.state.panel} onTabChange={
					(e) => this.setState({panel: e.index})
				}>
					<TabPanel header={Translate.value("ocr.general.label", {ns: "dialog"})}
							  headerClassName="control-tab-general" contentClassName="general-tab-panel">
						{this.renderGeneralTab(parameter!)}
					</TabPanel>
					<TabPanel header={Translate.value("ocr.optimization.label", {ns: "dialog"})}
							  headerClassName="control-tab-optimization" contentClassName="optimization-tab-panel">
						{this.renderOptimizationTab(parameter!)}
					</TabPanel>
					<TabPanel header={Translate.value("ocr.format.label", {ns: "dialog"})}
							  headerClassName="control-tab-format" contentClassName="format-tab-panel"
							  headerStyle={
								  this.state.fields?.contains(OcrDialogFields.SIZE) &&
								  parameter!.outputFormat === OcrOutput.Pdf ? {} : {display: "none"}
							  }>
						{this.renderFormatTab(parameter!)}
					</TabPanel>
				</TabView>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["ocr-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				{this.renderError()}
				<Helper {...this.props}>
					<Translate {...this.props} data={"dialog.ocr.general"} namespace={"help"} markdown={true}/>
				</Helper>
				<Helper {...this.props} type={MessageType.WARNING}>
					<Translate {...this.props} data={"dialog.ocr.dpi"} namespace={"help"} markdown={true}/>
				</Helper>
				{this.renderContents()}
			</Dialog>
		);
	}

	protected onChange(field: OcrDialogFields, value: any): void {
		let parameter: PortalOcr = this.state.parameter!.data;
		let pageParameter: PortalOcrPage = parameter.page as PortalOcrPage;

		switch (field) {
			case OcrDialogFields.OUTPUT_FORMAT:
				parameter.outputFormat = value;
				parameter.forceEachPage = parameter.outputFormat !== OcrOutput.Pdf;
				break;
			case OcrDialogFields.PDFA_FORMAT:
				parameter.pdfaMode = value;
				break;
			case OcrDialogFields.PDFA_RESOLUTION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ConvertPdfa.getImageQualityDefault();
				}

				parameter.pdfa!.convert!.imageQuality = value;
				break;
			case OcrDialogFields.LANGUAGE:
				parameter.language = value;
				break;
			case OcrDialogFields.JPEG_QUALITY:
				parameter.jpegQuality = value;
				break;
			case OcrDialogFields.DPI_ACTIVE:
				parameter.imageDpiActive = value;
				break;
			case OcrDialogFields.DPI:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Ocr.getImageDpiDefault();
				}

				parameter.imageDpi = value;
				break;
			case OcrDialogFields.NORMALIZE_ROTATION:
				parameter.normalizePageRotation = value;
				break;
			case OcrDialogFields.CHECK_RESOLUTION:
				parameter.checkResolution = value;
				break;
			case OcrDialogFields.FAIL_ON_WARNING:
				parameter.failOnWarning = value;
				break;
			case OcrDialogFields.OPTIMIZE:
				parameter.optimizationActive = value;
				break;
			case OcrDialogFields.NOISE_REDUCTION:
				parameter.optimization!.noiseReduction = value;
				break;
			case OcrDialogFields.GAMMA_CORRECTION:
				parameter.optimization!.gammaCorrection = value;
				break;
			case OcrDialogFields.INCREASE_CONTRAST:
				parameter.optimization!.increaseContrast = value;
				break;
			case OcrDialogFields.EDGE_ACCENTUATION:
				parameter.optimization!.edgeAccentuation = value;
				break;
			case OcrDialogFields.SHARPEN:
				parameter.optimization!.sharpen = value;
				break;
			case OcrDialogFields.MEDIAN_FILTER:
				parameter.optimization!.medianFilter = value;
				break;
			case OcrDialogFields.DESKEW:
				parameter.optimization!.deskew = value;
				break;
			case OcrDialogFields.DESPECKLE:
				parameter.optimization!.despeckle = value;
				break;
			case OcrDialogFields.DITHERING:
				parameter.optimization!.reduceDithering = value;
				break;
			case OcrDialogFields.PAGE_FORMAT:
				pageParameter.format = value;

				let format: PaperFormat | null = PaperHelper.findFormat(value);
				if (!FunctionHelper.exists(format)) {
					break;
				}

				let width: number =
					pageParameter.orientation === OrientationEnum.VERTICAL ? format!.width : format!.height;
				let height: number =
					pageParameter.orientation === OrientationEnum.VERTICAL ? format!.height : format!.width;
				pageParameter.width = width;
				pageParameter.height = height;
				break;
			case OcrDialogFields.HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OcrPage.getHeightDefault();
				}

				pageParameter.height = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case OcrDialogFields.WIDTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OcrPage.getWidthDefault();
				}

				pageParameter.width = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case OcrDialogFields.ORIENTATION:
				if (pageParameter.orientation === value) {
					break;
				}

				pageParameter.orientation = value;
				let tempSize: number = FunctionHelper.applyValue(pageParameter.width, OcrPage.getWidthDefault());
				pageParameter.width = FunctionHelper.applyValue(pageParameter.height, OcrPage.getHeightDefault());
				pageParameter.height = tempSize;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-ocr small"}/>
					<Translate {...this.props} data="ocr.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalOcr | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());

		let language: string = i18next.language;
		switch (language) {
			case 'de':
				parameter!.language = OcrLanguage.Deu;
				break;
			case 'fr':
				parameter!.language = OcrLanguage.Fra;
				break;
			case 'it':
				parameter!.language = OcrLanguage.Ita;
				break;
			case 'es':
				parameter!.language = OcrLanguage.Spa;
				break;
			default:
				parameter!.language = OcrLanguage.Eng;
				break;
		}

		this.setState({
			panel: 0,
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			unit: userSettings.unit
		});
	}

	protected async submit(): Promise<void> {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();
		let operationData: PortalOcr = this.state.parameter!.data.clone();

		for (let portalDocument of selection) {
			if (operationData.outputFormat === OcrOutput.Pdf && super.isEditPasswordNeeded(portalDocument)) {
				this.setState({
					showPasswordDialog: true,
					passwordDocument: portalDocument
				});
				return;
			}
		}

		await super.submit();

		if (operationData.outputFormat === OcrOutput.Pdf) {
			let pageParameter: PortalOcrPage = operationData.page as PortalOcrPage;

			if (pageParameter.format === FormatEnum.ORIGINAL || !this.state.fields?.contains(OcrDialogFields.SIZE)) {
				operationData.page = undefined;
			}
		} else {
			operationData.page = undefined;
		}

		if (operationData.pdfaMode === OcrPdfaType.NONE) {
			operationData.pdfa = undefined
		}

		if (!operationData.optimizationActive) {
			operationData.optimization = undefined;
		}

		let operation: ConcurrentOperation = new ConcurrentOperation(this.props.concurrentOperations);
		for (let portalDocument of selection) {
			operation.add(async (resolve: () => void, reject: (exception: Error) => void) => {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: WebServiceTypes.OCR,
						document: portalDocument,
						operation: Ocr.fromJson(operationData.toJson()),
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as Settings)
					});

					resolve();
				} catch (exception: any) {
					if (this.props.concurrentOperations > 1) {
						let portalException: Exception = Exception.parseException(exception);
						portalDocument.setExecutionError(portalException);
						documentManager.updateGlobalDocumentList();
					}

					reject(exception);
				}
			});
		}

		try {
			await operation.execute();
			let message: Message = new Message("success.ocr.ocr", MessageType.SUCCESS);
			message.context = {count: operation.length};
			this.props.context.getPage().addMessage(message);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
				return;
			}

			if (this.props.concurrentOperations > 1) {
				this.hide();
			}
			return;
		}

		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.OCR;
	}

	protected getDefaultParameter(): Parameter<PortalOcr> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			panel: 0
		});
	}
}

const OcrDialog = React.memo(OcrDialogDefinition,
	(prevProps: Readonly<OcrDialogProperties>, nextProps: Readonly<OcrDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true;
		}

		return false;
	}
);

export default OcrDialog;